/**
 * safe-render.ts
 * Safe inline Markdown and pluggable entity renderer — prompt-injection boundary for chat content.
 *
 * Supported tokens:
 *   **bold**            → SafeSegment { type: 'bold' }
 *   `code`              → SafeSegment { type: 'code' }
 *   [label](url)        → SafeSegment { type: 'link' }  — only https/http/mailto
 *   #123 / #task-123    → SafeSegment { type: 'taskbadge', entityId: '123' }
 *   @deal-101 / deal:101→ SafeSegment { type: 'dealbadge', entityId: '101' }
 *   @lead-202 / lead:202→ SafeSegment { type: 'leadbadge', entityId: '202' }
 *   @username / hns://  → SafeSegment { type: 'usermention', entityId: 'username' }
 *   wiki://…            → SafeSegment { type: 'wikibadge', entityId: '...' }
 *   \n                  → SafeSegment { type: 'linebreak' }
 *   (everything else)   → SafeSegment { type: 'text' }
 *
 * Explicitly blocked:
 *   - Arbitrary HTML / script tags / dangerouslySetInnerHTML
 *   - javascript: / data: / vbscript: link schemes
 *   - CSS hex color false positives (#ffffff, #0b0f19)
 *   - URL fragment false positives (https://...#section)
 *   - Zero-width character manipulation & Bidi overrides
 */

import { globalEntityLinkerRegistry } from './entity-linker-registry.js';

export type SafeSegmentType =
  | 'text'
  | 'bold'
  | 'code'
  | 'link'
  | 'linebreak'
  | 'taskbadge'
  | 'dealbadge'
  | 'leadbadge'
  | 'usermention'
  | 'wikibadge'
  | 'entity';

export interface SafeSegment {
  type: SafeSegmentType;
  content: string;
  href?: string;
  entityId?: string;
  pluginId?: string;
}

// ─── URL allowlist ────────────────────────────────────────────────────────────

function isSafeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.protocol === 'https:' ||
      u.protocol === 'http:' ||
      u.protocol === 'mailto:' ||
      u.protocol === 'hxn:' ||
      u.protocol === 'hns:'
    );
  } catch {
    return false;
  }
}

// ─── Tokenizer ────────────────────────────────────────────────────────────────

/**
 * Parse `text` into a flat array of SafeSegments with strict boundary verification
 * and pluggable entity reference support.
 */
export function safeRender(text: string): SafeSegment[] {
  if (!text) return [];

  // Bounded input length for ReDoS protection & normalize Unicode
  const safeText = text.length > 50000 ? text.slice(0, 50000) : text;
  const normalized = safeText
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E]/g, '');

  const segments: SafeSegment[] = [];

  // 1. Markdown syntax tokens
  // Order matters: bold, code, markdown link, linebreaks
  const MD_RE =
    /(\*\*([^*\n]+?)\*\*|`([^`\n]+?)`|\[([^\]\n]{1,200})\]\(((?:https?|hxn|hns):\/\/[^)\s]{1,2000}|mailto:[^)\s]{1,500})\)|\n)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = MD_RE.exec(normalized)) !== null) {
    // Intervening text slice between Markdown syntax matches
    if (match.index > lastIndex) {
      const intermediateText = normalized.slice(lastIndex, match.index);
      parseEntityAndTextSegments(intermediateText, segments);
    }

    const [
      full,
      ,            // group 1: full bold match
      boldContent, // group 2
      codeContent, // group 3
      linkLabel,   // group 4
      linkHref,    // group 5
    ] = match;

    if (boldContent !== undefined) {
      // Recursively parse bold content for entity references (#88, @deal-101, wiki://, etc.)
      // This ensures **#88** renders as a clickable bold taskbadge, not plain bold text.
      const innerMatches = globalEntityLinkerRegistry.parseEntities(boldContent);
      if (innerMatches.length > 0) {
        // Emit entity segments from inside the bold span
        let innerIdx = 0;
        for (const em of innerMatches) {
          if (em.start > innerIdx) {
            segments.push({ type: 'bold', content: boldContent.slice(innerIdx, em.start) });
          }
          const eType = em.definition.entityType;
          let segType: SafeSegmentType = 'entity';
          if (eType === 'task') segType = 'taskbadge';
          else if (eType === 'deal') segType = 'dealbadge';
          else if (eType === 'lead') segType = 'leadbadge';
          else if (eType === 'user') segType = 'usermention';
          else if (eType === 'wiki') segType = 'wikibadge';
          segments.push({
            type: segType,
            content: em.rawMatch.trim(),
            entityId: em.extractedId,
            pluginId: em.definition.pluginId,
          });
          innerIdx = em.end;
        }
        if (innerIdx < boldContent.length) {
          segments.push({ type: 'bold', content: boldContent.slice(innerIdx) });
        }
      } else {
        segments.push({ type: 'bold', content: boldContent });
      }
    } else if (codeContent !== undefined) {
      segments.push({ type: 'code', content: codeContent });
    } else if (linkLabel !== undefined && linkHref !== undefined && isSafeUrl(linkHref)) {
      segments.push({ type: 'link', content: linkLabel, href: linkHref });
    } else if (linkLabel !== undefined) {
      segments.push({ type: 'text', content: `[${linkLabel}](blocked)` });
    } else if (full === '\n') {
      segments.push({ type: 'linebreak', content: '' });
    } else {
      segments.push({ type: 'text', content: full });
    }

    lastIndex = MD_RE.lastIndex;
  }

  // Remainder after last Markdown token
  if (lastIndex < normalized.length) {
    parseEntityAndTextSegments(normalized.slice(lastIndex), segments);
  }

  return segments;
}

/**
 * Scans plain text spans for registered entity tokens (tasks, deals, leads, user mentions, wiki).
 */
function parseEntityAndTextSegments(plainText: string, segments: SafeSegment[]): void {
  if (!plainText) return;

  const entityMatches = globalEntityLinkerRegistry.parseEntities(plainText);
  if (entityMatches.length === 0) {
    segments.push({ type: 'text', content: plainText });
    return;
  }

  let currIdx = 0;

  for (const m of entityMatches) {
    if (m.start > currIdx) {
      segments.push({ type: 'text', content: plainText.slice(currIdx, m.start) });
    }

    const eType = m.definition.entityType;
    let segType: SafeSegmentType = 'entity';

    if (eType === 'task') segType = 'taskbadge';
    else if (eType === 'deal') segType = 'dealbadge';
    else if (eType === 'lead') segType = 'leadbadge';
    else if (eType === 'user') segType = 'usermention';
    else if (eType === 'wiki') segType = 'wikibadge';

    segments.push({
      type: segType,
      content: m.rawMatch.trim(),
      entityId: m.extractedId,
      pluginId: m.definition.pluginId,
    });

    currIdx = m.end;
  }

  if (currIdx < plainText.length) {
    segments.push({ type: 'text', content: plainText.slice(currIdx) });
  }
}
